/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useCallback, useEffect, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import type { Location } from 'history';
import URI from 'jsuri';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useLocation, useParams } from 'js/lib/useRouter';
import { get as getUser } from 'js/lib/user';

import { Link } from '@coursera/cds-core';
import { Box } from '@coursera/coursera-ui';

import { getCertificatePDFRenderingErrors } from 'bundles/accomplishments/api/certificateRenderErrorsAPI';
import { byVerifyCode } from 'bundles/accomplishments/api/certificatesAPI';
import { getS12nProductVariantById } from 'bundles/accomplishments/api/s12nAPI';
import PdfPageHeader from 'bundles/accomplishments/components/pdf-page/PdfPageHeader';
import VerificationIncompleteModal from 'bundles/accomplishments/components/pdf-page/VerificationIncompleteModal';
import membershipPromise from 'bundles/accomplishments/promises/membership';
import s12nMembershipPromise from 'bundles/accomplishments/promises/s12nMembership';
import specializationMembershipPromise from 'bundles/accomplishments/promises/specializationMembership';
import { org_coursera_userverification_UserVerificationRequirementState as VerificationReqs } from 'bundles/accomplishments/queries/hooks/__generated__/globalTypes';
import { useLearnerVerificationRequirements } from 'bundles/accomplishments/queries/hooks/useLearnerVerificationRequirements';
import { CertificatePlatformOrigin } from 'bundles/accomplishments/types';
import {
  getCertificateDownloadLink,
  getCourseCertificateVerifyLink,
  getSpecializationCertificateRecordsLink,
} from 'bundles/accomplishments/utils/routingUtils';
import type Membership from 'bundles/catalogP/models/membership';
import { NotFound } from 'bundles/common/components/withNotFound';
import LoadingIcon from 'bundles/courseraLoadingIcon/LoadingIcon';

import _t from 'i18n!nls/accomplishments';

// I do not know why hostname and protocol are left out of the official types even though they exist in reality
type LocationWithHostnameAndProtocol = Location & { hostname: string; protocol: string };

const CourseCertificatePDF = () => {
  const { code } = useParams();
  const { pathname, protocol, hostname } = useLocation() as LocationWithHostnameAndProtocol;
  const isS12n = /specialization/.test(pathname);
  const shareLink = new URI()
    .setProtocol(protocol)
    .setHost(hostname)
    .setPath(`/account/accomplishments/${isS12n ? 'specialization' : 'verify'}/${code}`)
    .toString();
  const apolloClient = useApolloClient();

  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [pdfDownloadLink, setPdfDownloadLink] = useState('');
  const [landingPageLink, setLandingPageLink] = useState('');
  const [certificateRenderErrors, setCertificateRenderErrors] = useState<string[]>([]);
  const [productName, setProductName] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [slug, setSlug] = useState('');
  const [loading, setLoading] = useState(true);
  const [isCertificateOwner, setIsCertificateOwner] = useState(false);
  const [certificateUserId, setCertificateUserId] = useState('');

  const fetchCertificate = useCallback<() => $TSFixMe>(async () => {
    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    const responses = await Promise.all([byVerifyCode(code), getCertificatePDFRenderingErrors(code)]);

    if (responses.length > 0) {
      const [certificate, certRenderErrors] = responses;

      if (certificate) {
        const { userId: certUserId, platformOrigin } = certificate;
        const isSparkCertificate = platformOrigin === CertificatePlatformOrigin.SPARK;
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        const certDownloadLink = getCertificateDownloadLink(certUserId, isS12n, code, isSparkCertificate);

        let certLandingPageLink: string;
        if (isS12n) {
          const productVariant = await getS12nProductVariantById(certificate.certificateItemId, apolloClient);
          // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          certLandingPageLink = getSpecializationCertificateRecordsLink(productVariant, code);
        } else {
          // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          certLandingPageLink = getCourseCertificateVerifyLink(code);
        }

        const userIdOfCurrentUser = getUser().id;
        const isCertOwner = certUserId === String(userIdOfCurrentUser);

        setPdfDownloadLink(certDownloadLink);
        setLandingPageLink(certLandingPageLink);
        setCertificateRenderErrors(certRenderErrors);
        setIsCertificateOwner(isCertOwner);
        setCertificateUserId(certUserId);
      }
    }
    setLoading(false);
  }, [code, isS12n, apolloClient]);

  const fetchMembership = useCallback<() => $TSFixMe>(async () => {
    let membership: typeof Membership;

    if (isS12n) {
      const responses = await Promise.all([specializationMembershipPromise(code), s12nMembershipPromise(code)]);
      const specializationResponse = responses.find((response) => response.state === 'fulfilled' && response.value);
      membership = specializationResponse && specializationResponse.value;
    } else {
      membership = await membershipPromise(code, false);
    }

    const productMembership = isS12n ? membership.get('specialization') : membership.get('course');

    setProductName(productMembership.get('name'));
    setPartnerName(productMembership.get('partners').getNamesString());
    setSlug(productMembership.get('slug'));
  }, [code, isS12n]);

  useEffect(() => {
    fetchCertificate();
    fetchMembership();
  }, [fetchCertificate, fetchMembership]);

  const { verificationRequirements } = useLearnerVerificationRequirements(certificateUserId);

  useEffect(() => {
    if (verificationRequirements === VerificationReqs.NEEDS_TO_ATTEST_NAME) {
      setIsVerificationModalOpen(true);
    }
  }, [verificationRequirements]);

  if (loading) {
    return (
      <div className="rc-CertificatePdf">
        <LoadingIcon />
      </div>
    );
  }

  if (!pdfDownloadLink) {
    return <NotFound />;
  }

  const certType = isS12n ? 'Specialization' : 'Course';

  return (
    <div className="rc-CertificatePdf">
      <PdfPageHeader
        shareLink={shareLink}
        certType={certType}
        landingPageLink={landingPageLink}
        pdfDownloadLink={pdfDownloadLink}
        productName={productName}
        partnerName={partnerName}
        isS12n={isS12n}
        slug={slug}
      />
      <VerificationIncompleteModal
        open={isVerificationModalOpen}
        isCertificateOwner={isCertificateOwner}
        onClose={() => setIsVerificationModalOpen(false)}
      />
      <div
        className="certificate-pdf-container"
        css={css`
          background: var(--cds-color-grey-700);
          height: calc(100vh - 70px);
          width: 100%;
          overflow-x: auto;
        `}
      >
        {certificateRenderErrors.length > 0 ? (
          <Box justifyContent="center">
            <div className="error-message-container">
              <FormattedMessage message={_t('Attempted to fetch PDF but got the following errors:')} tagName="b" />
              <div>{certificateRenderErrors.join('\n')}</div>
            </div>
          </Box>
        ) : (
          <object
            data-js="certificate-pdf"
            data-e2e="certificate-pdf"
            type="application/pdf"
            data={pdfDownloadLink}
            className="certificate-pdf"
            aria-label={_t('Certificate PDF')}
            css={css`
              width: 1000px;
              height: 900px;
              background: var(--cds-color-grey-700);
              /* stylelint-disable-next-line declaration-block-no-duplicate-properties */
              height: calc(100vh - 70px);
              /* stylelint-disable-next-line declaration-block-no-duplicate-properties */
              width: 100%;
              overflow-x: auto;
            `}
          >
            <div className="rc-CertificateRenderErrorMessageContainer">
              <FormattedMessage
                message={_t('Your web browser may not have a PDF plugin. Instead you can {clickDownloadLink}')}
                clickDownloadLink={<Link href={pdfDownloadLink}>{_t('click here to download the PDF file.')}</Link>}
              />
            </div>
          </object>
        )}
      </div>
    </div>
  );
};

export default CourseCertificatePDF;
